// src/hooks/useDashboardData.js - Dashboard data fetching with session cache + background polling
import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';

const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes
const CACHE_TTL = 5 * 60 * 1000;     // 5 minutes (matches poll interval)

// Module-level session cache — survives component unmounts during navigation
const _sessionCache = {
  data: null,
  lastFetched: null,
  userId: null,
};

function _getCached(userId) {
  if (_sessionCache.userId !== userId) return null;
  if (_sessionCache.lastFetched && (Date.now() - _sessionCache.lastFetched) > CACHE_TTL) {
    _sessionCache.data = null;
    _sessionCache.lastFetched = null;
  }
  return _sessionCache;
}

function _updateCache(userId, data) {
  _sessionCache.userId = userId;
  _sessionCache.data = data;
  _sessionCache.lastFetched = Date.now();
}

const useDashboardData = (user) => {
  const cached = _getCached(user?.uid);
  const [data, setData] = useState(cached?.data || null);
  const [loading, setLoading] = useState(!cached?.data); // skip loading if cached
  const [error, setError] = useState(null);
  const [authError, setAuthError] = useState(false);
  const [lastFetched, setLastFetched] = useState(
    cached?.lastFetched ? new Date(cached.lastFetched) : null
  );
  const intervalRef = useRef(null);

  const fetchDashboard = useCallback(async (isBackground = false) => {
    if (!user?.uid) return;

    if (!isBackground) setLoading(true);
    setError(null);

    try {
      const response = await api.get('/v1/dashboard');
      setData(response.data);
      setLastFetched(new Date());
      setAuthError(false);

      // Persist to session cache
      _updateCache(user.uid, response.data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setAuthError(true);
      }
      if (!isBackground) {
        setError(err?.response?.data?.detail?.message || err.message || 'Failed to load dashboard');
      }
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [user?.uid]);

  // Initial fetch on mount — skip if cache is fresh
  useEffect(() => {
    const cached = _getCached(user?.uid);
    if (cached?.data && cached?.lastFetched) {
      // Cache is fresh — no need to fetch, but do a background refresh
      fetchDashboard(true);
    } else {
      // No cache or expired — full fetch with loading spinner
      fetchDashboard(false);
    }
  }, [fetchDashboard, user?.uid]);

  // Background polling every 5 minutes (stop if auth failed)
  useEffect(() => {
    if (authError) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    intervalRef.current = setInterval(() => {
      fetchDashboard(true);
    }, POLL_INTERVAL);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchDashboard, authError]);

  // Manual refresh (always hits API with loading state)
  const refresh = useCallback(() => {
    fetchDashboard(false);
  }, [fetchDashboard]);

  return {
    // Market data
    indices: data?.market?.indices || {},
    sentiment: data?.market?.sentiment || 'neutral',

    // Portfolio (null if no holdings)
    portfolio: data?.portfolio || null,
    hasHoldings: data?.portfolio !== null && data?.portfolio !== undefined,

    // AI briefing
    aiBriefing: data?.ai_briefing || null,

    // News
    news: data?.news || [],

    // Earnings
    earnings: data?.upcoming_earnings || [],

    // Meta
    loading,
    error,
    authError,
    lastFetched,
    refresh
  };
};

export default useDashboardData;
